/**
 * Creazione in blocco (V12), in un modale: giorni della settimana, ora,
 * periodo e luogo, poi l'anteprima obbligatoria delle date generate. Si apre
 * dagli allenamenti (V10), anche vuoti, e dal cruscotto della squadra in
 * preparazione.
 *
 * Nell'anteprima le date con una partita della squadra lo stesso giorno sono
 * evidenziate ma restano scelte: e un avviso. Quelle con un allenamento gia
 * alla stessa ora non si possono scegliere. Si confermano le date spuntate;
 * se nel frattempo e cambiato qualcosa il server rifiuta e rimanda
 * l'anteprima aggiornata, che sostituisce quella a schermo tenendo le scelte.
 */

import { type FormEvent, useState } from 'react';
import type { BloccoAllenamenti as Regola } from '@calcio/shared';
import { bloccoAllenamentiSchema, dataLocale } from '@calcio/shared';
import { OctagonXIcon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { ErroreApi, chiama } from '../api/client.js';
import { CampoData } from '../comuni/CampoData.js';
import { CampoSuggerito } from '../comuni/CampoSuggerito.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import {
  type DataInAnteprima,
  type OpzioniAllenamento,
  formatoOra,
  formatoRiga,
} from './allenamenti.js';
import { Nota } from '../comuni/Nota.js';

const GIORNI = [
  { valore: 1, etichetta: 'Lun' },
  { valore: 2, etichetta: 'Mar' },
  { valore: 3, etichetta: 'Mer' },
  { valore: 4, etichetta: 'Gio' },
  { valore: 5, etichetta: 'Ven' },
  { valore: 6, etichetta: 'Sab' },
  { valore: 7, etichetta: 'Dom' },
];

export function DialogBlocco({
  ss,
  aperto,
  onChiudi,
  onCreati,
}: {
  ss: string;
  aperto: boolean;
  onChiudi: () => void;
  /** Dopo la conferma, a modale gia chiuso. */
  onCreati: () => Promise<void> | void;
}) {
  return (
    <Dialog open={aperto} onOpenChange={(a) => !a && onChiudi()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        {aperto && <Caricamento ss={ss} onChiudi={onChiudi} onCreati={onCreati} />}
      </DialogContent>
    </Dialog>
  );
}

function Caricamento({
  ss,
  onChiudi,
  onCreati,
}: {
  ss: string;
  onChiudi: () => void;
  onCreati: () => Promise<void> | void;
}) {
  const opzioni = useDati<OpzioniAllenamento>(`/squadre-stagione/${ss}/allenamenti/opzioni`);
  if (!opzioni.dati) {
    return (
      <>
        <Intestazione />
        <StatoCaricamento errore={opzioni.errore} />
      </>
    );
  }
  return <Blocco ss={ss} opzioni={opzioni.dati} onChiudi={onChiudi} onCreati={onCreati} />;
}

function Intestazione() {
  return (
    <DialogHeader>
      <DialogTitle>Allenamenti in blocco</DialogTitle>
      <DialogDescription>
        Giorni della settimana, ora e periodo: prima di creare vedi le date e togli quelle da
        saltare.
      </DialogDescription>
    </DialogHeader>
  );
}

/** Oggi, o l'inizio della stagione se non e ancora cominciata; la fine a un mese, dentro la stagione. */
function periodoIniziale({ dataInizio, dataFine }: OpzioniAllenamento['stagione']) {
  const oggi = dataLocale(new Date());
  const da = oggi < dataInizio ? dataInizio : oggi > dataFine ? dataInizio : oggi;
  const traUnMese = dataLocale(new Date(new Date(`${da}T12:00:00Z`).getTime() + 30 * 86_400_000));
  return { da, a: traUnMese > dataFine ? dataFine : traUnMese };
}

function Blocco({
  ss,
  opzioni,
  onChiudi,
  onCreati,
}: {
  ss: string;
  opzioni: OpzioniAllenamento;
  onChiudi: () => void;
  onCreati: () => Promise<void> | void;
}) {
  const iniziale = periodoIniziale(opzioni.stagione);
  const [giorni, setGiorni] = useState<string[]>([]);
  const [ora, setOra] = useState('18:00');
  const [da, setDa] = useState(iniziale.da);
  const [a, setA] = useState(iniziale.a);
  const [luogo, setLuogo] = useState(opzioni.luoghi[0] ?? '');
  const [note, setNote] = useState('');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const [anteprima, setAnteprima] = useState<{ regola: Regola; date: DataInAnteprima[] } | null>(
    null,
  );
  const [escluse, setEscluse] = useState<Set<string>>(new Set());
  const [cambiata, setCambiata] = useState(false);

  // Cambiando la regola l'anteprima non descrive piu cio che si confermerebbe.
  const cambia =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setAnteprima(null);
      setCambiata(false);
      setErroreServer(null);
    };

  const chiediAnteprima = async (e: FormEvent) => {
    e.preventDefault();
    const esito = bloccoAllenamentiSchema.safeParse({
      giorni: giorni.map(Number),
      ora,
      da,
      a,
      luogo,
      note,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      const { date } = await chiama<{ date: DataInAnteprima[] }>(
        `/squadre-stagione/${ss}/allenamenti/blocco/anteprima`,
        { metodo: 'POST', corpo: esito.data },
      );
      setAnteprima({ regola: esito.data, date });
      setEscluse(new Set(date.filter((d) => d.duplicato).map((d) => d.dataOra)));
      setCambiata(false);
    } catch (errore) {
      setErroreServer(messaggio(errore));
    } finally {
      setInCorso(false);
    }
  };

  const scelte = anteprima?.date.filter((d) => !d.duplicato && !escluse.has(d.dataOra)) ?? [];

  const conferma = async () => {
    if (!anteprima || scelte.length === 0) return;
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/allenamenti/blocco`, {
        metodo: 'POST',
        corpo: {
          luogo: anteprima.regola.luogo,
          note: anteprima.regola.note,
          date: scelte.map((d) => ({
            dataOra: d.dataOra,
            partiteViste: d.partite.map((p) => p.id),
          })),
        },
      });
      toast.success(
        scelte.length === 1 ? 'Creato 1 allenamento.' : `Creati ${scelte.length} allenamenti.`,
      );
      onChiudi();
      await onCreati();
    } catch (errore) {
      const aggiornata = (errore instanceof ErroreApi ? errore.corpo : undefined) as
        { anteprima?: { date: DataInAnteprima[] } } | undefined;
      if (aggiornata?.anteprima) {
        // La risposta copre le sole date confermate: le altre restano come
        // erano, e i nuovi doppioni escono dalla scelta.
        const nuove = new Map(aggiornata.anteprima.date.map((d) => [d.dataOra, d]));
        setAnteprima({
          ...anteprima,
          date: anteprima.date.map((d) => nuove.get(d.dataOra) ?? d),
        });
        setCambiata(true);
      } else {
        setErroreServer(messaggio(errore));
      }
      setInCorso(false);
    }
  };

  const { dataInizio, dataFine } = opzioni.stagione;

  return (
    <div className="flex flex-col gap-6">
      <Intestazione />
      <form onSubmit={(e) => void chiediAnteprima(e)} className="flex flex-col gap-6">
        <FieldGroup>
          <Field data-invalid={Boolean(errori.giorni)}>
            <FieldLabel>Giorni della settimana</FieldLabel>
            <ToggleGroup
              type="multiple"
              variant="outline"
              value={giorni}
              onValueChange={cambia(setGiorni)}
              className="flex-wrap"
            >
              {GIORNI.map((g) => (
                <ToggleGroupItem key={g.valore} value={String(g.valore)} className="min-w-12">
                  {g.etichetta}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <FieldError>{errori.giorni}</FieldError>
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field data-invalid={Boolean(errori.ora)}>
              <FieldLabel htmlFor="blocco-ora">Ora</FieldLabel>
              <Input
                id="blocco-ora"
                type="time"
                value={ora}
                aria-invalid={Boolean(errori.ora)}
                onChange={(e) => cambia(setOra)(e.target.value)}
              />
              <FieldError>{errori.ora}</FieldError>
            </Field>
            <Field data-invalid={Boolean(errori.da)}>
              <FieldLabel htmlFor="blocco-da">Dal</FieldLabel>
              <CampoData
                id="blocco-da"
                valore={da}
                min={dataInizio}
                max={dataFine}
                invalido={Boolean(errori.da)}
                onCambia={cambia(setDa)}
              />
              <FieldError>{errori.da}</FieldError>
            </Field>
            <Field data-invalid={Boolean(errori.a)}>
              <FieldLabel htmlFor="blocco-a">Al</FieldLabel>
              <CampoData
                id="blocco-a"
                valore={a}
                min={da || dataInizio}
                max={dataFine}
                invalido={Boolean(errori.a)}
                onCambia={cambia(setA)}
              />
              <FieldError>{errori.a}</FieldError>
            </Field>
          </div>
          <FieldDescription>
            La stagione va dal {dataBreve(dataInizio)} al {dataBreve(dataFine)}.
          </FieldDescription>

          <Field data-invalid={Boolean(errori.luogo)}>
            <FieldLabel htmlFor="blocco-luogo">Luogo</FieldLabel>
            <CampoSuggerito
              id="blocco-luogo"
              valore={luogo}
              opzioni={opzioni.luoghi}
              onCambia={cambia(setLuogo)}
              segnaposto="Scegli o scrivi il luogo"
              nuovo={(t) => `Usa «${t}»`}
              invalido={Boolean(errori.luogo)}
            />
            <FieldError>{errori.luogo}</FieldError>
          </Field>

          <Field data-invalid={Boolean(errori.note)}>
            <FieldLabel htmlFor="blocco-note">Note</FieldLabel>
            <Textarea
              id="blocco-note"
              value={note}
              aria-invalid={Boolean(errori.note)}
              onChange={(e) => cambia(setNote)(e.target.value)}
            />
            <FieldDescription>Le stesse note per tutti gli allenamenti creati.</FieldDescription>
            <FieldError>{errori.note}</FieldError>
          </Field>
        </FieldGroup>

        {erroreServer && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{erroreServer}</AlertDescription>
          </Alert>
        )}

        {!anteprima && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onChiudi}>
              Annulla
            </Button>
            <PulsanteInvio type="submit" inCorso={inCorso}>
              Mostra le date
            </PulsanteInvio>
          </DialogFooter>
        )}
      </form>

      {anteprima && (
        <section className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">
            Anteprima · {scelte.length} su {anteprima.date.length}
          </h2>
          {cambiata && (
            <Alert>
              <TriangleAlertIcon />
              <AlertTitle>Nel frattempo è cambiato qualcosa</AlertTitle>
              <AlertDescription>
                Le date qui sotto sono aggiornate: controllale e conferma di nuovo.
              </AlertDescription>
            </Alert>
          )}
          <Nota>
            Togli la spunta alle date da saltare. Le partite dello stesso giorno sono solo un
            avviso: l'allenamento si crea comunque, se resta spuntato.
          </Nota>
          <ul className="border-t border-border">
            {anteprima.date.map((d) => {
              const id = `data-${d.dataOra}`;
              const conPartita = d.partite.length > 0;
              return (
                <li
                  key={d.dataOra}
                  className={cn(
                    'flex items-start gap-3 border-b border-border px-1 py-3',
                    conPartita && !d.duplicato && 'bg-muted',
                    d.duplicato && 'text-muted-foreground',
                  )}
                >
                  <Checkbox
                    id={id}
                    className="mt-1"
                    disabled={d.duplicato}
                    checked={!d.duplicato && !escluse.has(d.dataOra)}
                    onCheckedChange={(v) =>
                      setEscluse((s) => {
                        const n = new Set(s);
                        if (v) n.delete(d.dataOra);
                        else n.add(d.dataOra);
                        return n;
                      })
                    }
                  />
                  <label htmlFor={id} className="flex flex-1 flex-col gap-1">
                    <span className="font-medium first-letter:uppercase">
                      {formatoRiga.format(new Date(d.dataOra))}
                    </span>
                    {d.duplicato && (
                      <span className="text-sm">C'è già un allenamento a quest'ora.</span>
                    )}
                    {d.partite.map((p) => (
                      <span key={p.id} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">Partita</Badge>
                        {formatoOra.format(new Date(p.dataOra))} · {p.inCasa ? 'Casa' : 'Trasferta'}{' '}
                        · {p.avversario}
                      </span>
                    ))}
                  </label>
                </li>
              );
            })}
          </ul>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAnteprima(null)}
              disabled={inCorso}
            >
              Cambia i dati
            </Button>
            <PulsanteInvio
              type="button"
              inCorso={inCorso}
              disabled={scelte.length === 0}
              onClick={() => void conferma()}
            >
              {scelte.length === 1 ? 'Crea 1 allenamento' : `Crea ${scelte.length} allenamenti`}
            </PulsanteInvio>
          </DialogFooter>
        </section>
      )}
    </div>
  );
}
